import { defineStore } from 'pinia';
import { ref } from 'vue';

import { getApiClient } from '../api/client';
import { fetchMenuTree, type MenuTreeNode } from '../api/menus';

/**
 * 메뉴 스토어.
 *
 * 책임:
 * - GET /api/menus/tree 응답을 캐시
 * - 사이드바 / 동적 라우팅이 공통으로 참조
 *
 * 정책:
 * - 싱글턴 axios 인스턴스(`getApiClient()`) 사용 — 토큰 자동 첨부 인터셉터 공유.
 * - 로그아웃 / 테넌트 전환 시 `invalidate()` 로 캐시 비움.
 * - 실패 시 빈 배열로 두고 UI 에서 안내. 라우터 가드는 막지 않는다 (사이드바
 *   부재만 발생). 401 인 경우 client 인터셉터가 /login 으로 redirect 한다.
 */
export const useMenuStore = defineStore('menu', () => {
  const tree = ref<MenuTreeNode[]>([]);
  const loaded = ref(false);
  const loading = ref(false);
  const error = ref<string | null>(null);

  async function load(force = false): Promise<void> {
    if (loaded.value && !force) return;
    if (loading.value) return;

    loading.value = true;
    error.value = null;
    try {
      tree.value = await fetchMenuTree(getApiClient());
      loaded.value = true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      error.value = message;
      tree.value = [];
    } finally {
      loading.value = false;
    }
  }

  function invalidate(): void {
    tree.value = [];
    loaded.value = false;
    loading.value = false;
    error.value = null;
  }

  return {
    tree,
    loaded,
    loading,
    error,
    load,
    invalidate,
  };
});
